<script setup lang="ts">
import { getHomePageApi } from '~/api'
import type { ReturnPromiseType } from '~/types/base'

definePageMeta({
  // 首页特殊处理透明
  middleware: () => {
    const { setConf } = useLayout()

    setConf({
      mode: 'dark',
      head: {
        bg: 'bg-transparent',
      },
      foot: {
        topBar: true,
      },
    })
  },
})

const pageKey = computed(() => 'home-index')

const { value, addToCache } = await useDataCache<ReturnPromiseType<typeof getHomePageApi>>(unref(pageKey))
const { data, error } = await useAsyncData(unref(pageKey), async () => {
  if (value) {
    return value
  }

  const response = await getHomePageApi()
  addToCache(response)
  return response
}, {
  getCachedData: (key, nuxt): ReturnPromiseType<typeof getHomePageApi> => nuxt.payload.data[key] || nuxt.static.data[key],
  lazy: import.meta.client,
  transform: (res) => {
    res[1].games = (res[1].games || []).slice(0, 16)
    return res
  },
})

whenever(error, () => {
  useRouteCache(route => route.setUncacheable())
  throw createError({ statusCode: 404, statusMessage: `${unref(pageKey)} has some error`, fatal: true })
}, { immediate: true })

if (!isProd()) {
  whenever(data, v => clientLog.log(unref(pageKey), v), { immediate: true })
}

// const { locale, locales } = useI18n()
// const route = useRoute()

// async function handleIp() {
//   const code = await getIpAreaApi()
//   // ip在映射表并且和当前语言不符 并且没有携带参数 需要跳转到指定语言的官网
//   if (isMatchInLocales(code) && code !== locale.value && !route.query.from) {
//     const localeConf = locales.value.find(i => i.code === code)!
//     const realUrl = withQuery(localeConf.domain!, { ...route.query, from: locale.value })
//     isDev() ? clientLog.log('线上环境会跳转到正确的url', realUrl) : window.location.replace(realUrl)
//   }
// }

// onMounted(handleIp)

// 首屏顶部背景是透明
const { autoSideBar } = useLayout()
autoSideBar((height, scrollY) => ({
  head: {
    bg: scrollY <= (height * 0.9) ? 'bg-transparent' : 'bg-[var(--bg-color)]',
  },
}))

useCacheRoute()

const { indexHead } = usePageHead()
indexHead()
</script>

<template>
  <div v-track="{ id: 'web-index', view: { value: { page: 'index' } } }" bg="no-repeat cover [var(--bg-color)]" class="-mt-[--header-height]" pb="80px md:160px">
    <HomeHeadBanner />

    <section class="relative of-hidden">
      <div class="view-content" lt-md="flex flex-nowrap overflow-x-auto gap-20px [&::-webkit-scrollbar]:size-0px">
        <HomeDescBox step="1" />
        <HomeDescBox class="md:mt--52px" step="2" flip />
        <HomeDescBox class="md:mt--52px" step="3" />
      </div>
      <img class="pointer-events-none absolute h-961px w-804px lt-md:hidden flip-rtl" inset="is-0 be--220px" src="/home/sec-bg.webp" loading="lazy" alt="sec-bg">
    </section>

    <section class="relative of-hidden">
      <HomeHotAppBox class="view-content" mt="80px md:100px" :list="data?.[0]?.hotApps" />

      <HomePreAppBox class="view-content" mt="80px md:160px" :list="data?.[0]?.preApps" />

      <HomeEasyfunAppBox class="view-content" mt="80px md:160px" :list="data?.[1]?.games" />

      <img class="pointer-events-none absolute h-985px w-651px lt-md:hidden flip-rtl" inset="ie-0 be--29px" src="/home/trd-bg.webp" loading="lazy" alt="trd-bg">
    </section>

    <section class="view-content" mt="80px md:160px">
      <div class="mx-auto w-fit flex" mb="28px md:60px">
        <Icon class="v-sub text-26px c-yellow-5 lt-md:hidden" name="Lightning" />
        <h2 class="select-none c-#ffffffe5 font-800" mx="20px md:14px" text="center 18px md:32px" lh="28px md:32px">
          {{ $t('home_page.Q&A') }}
        </h2>
        <Icon class="v-sub text-26px c-yellow-5 lt-md:hidden" name="Lightning" />
      </div>

      <div class="flex flex-col gap-16px md:gap-20px">
        <template v-for="i in 4" :key="i">
          <HomeFaqGroup :step="i" :init-visible="i === 1" />
        </template>
      </div>
    </section>
  </div>
</template>
